import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ACCESS_SYNC_WAIT_MS,
  classifyAssignError,
  type AccessSetupStatus,
} from '../utils/access-setup';

export interface UseAccessSetupOptions {
  projectId: string;
  /** Where to go once access has had time to propagate. */
  returnUrl: string | null;
  /**
   * Performs the role assignment. Must REJECT when the call failed (a resolved
   * urql result carrying `error` has to be turned into a throw by the caller),
   * so `classifyAssignError` can see the GraphQL error codes.
   */
  run: (projectId: string) => Promise<unknown>;
  /** Called once, ACCESS_SYNC_WAIT_MS after a successful assignment. */
  onGranted: (returnUrl: string | null) => void;
}

export interface UseAccessSetupResult {
  status: AccessSetupStatus;
  /** Restarts the cycle; a no-op unless the status is `failed`. */
  retry: () => void;
}

export const useAccessSetup = ({
  projectId,
  returnUrl,
  run,
  onGranted,
}: UseAccessSetupOptions): UseAccessSetupResult => {
  const [status, setStatusState] = useState<AccessSetupStatus>('loading');
  // Read synchronously by `retry` so two clicks in one tick cannot start two cycles.
  const statusRef = useRef<AccessSetupStatus>('loading');
  const setStatus = useCallback((next: AccessSetupStatus) => {
    statusRef.current = next;
    setStatusState(next);
  }, []);

  // Latest props without re-creating `start` (and re-running the mount effect).
  const latest = useRef({ projectId, returnUrl, run, onGranted });
  latest.current = { projectId, returnUrl, run, onGranted };

  // Armed in the effect SETUP, not only here: StrictMode replays setup -> cleanup
  // -> setup, and a cleanup-only ref would stay false for the component's life.
  const mountedRef = useRef(false);
  const startedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    setStatus('loading');
    const { projectId: id, run: doRun } = latest.current;
    let promise: Promise<unknown>;
    try {
      promise = Promise.resolve(doRun(id));
    } catch (error) {
      promise = Promise.reject(error);
    }
    promise.then(
      () => {
        if (!mountedRef.current) {
          return;
        }
        clearTimer();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setStatus('granted');
          latest.current.onGranted(latest.current.returnUrl);
        }, ACCESS_SYNC_WAIT_MS);
      },
      (error: unknown) => {
        if (!mountedRef.current) {
          return;
        }
        setStatus(classifyAssignError(error));
      }
    );
  }, [clearTimer, setStatus]);

  useEffect(() => {
    mountedRef.current = true;
    // One-shot per mount of the component, surviving a StrictMode replay: the
    // replayed setup sees `startedRef` already set and does not call `run` again,
    // while the in-flight call still finds `mountedRef` true when it settles.
    if (!startedRef.current) {
      startedRef.current = true;
      start();
    }
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [start, clearTimer]);

  const retry = useCallback(() => {
    if (statusRef.current !== 'failed') {
      return;
    }
    start();
  }, [start]);

  return { status, retry };
};
